import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../envirorments/environment';

// Define interfaces for request/response models
export interface SignupRequest {
  firstName: string;
  lastName: string;
  mobile: string;
  email: string;
  pan: string;
}


export interface OtpVerificationRequest {
  mobileNumber: string;
  otp: string;
  eventType: string;
}

export interface UserDetailsRequest {
  dateOfBirth: string;
  gender: string;
  pinCode: string;
  city: string;
  state: string;
}

export interface EmploymentDetailsRequest {
  employmentType: string;
  monthlyTurnover: string;
}

export interface PasswordRequest {
  password: string;
  confirmPassword: string;
}

export interface LoginRequest {
  emailId: string;
  password: string;
}

export interface LoginOtpRequest {
  mobile: string;
  loginType: string; // 'email' or 'mobile'
}

export interface AuthResponse {
  success: boolean;
  status: boolean;
  message: string;
  token?: string;
  userData?: any;
}

/**
 * Authentication service handling user registration, login, and token management
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private readonly authUrl = environment.authUrl;
  private readonly partnerUrl = environment.partnerBaseUrl;

  // Authentication state observable
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  // Store temporary user data during registration process
  private registrationData: any = {};

  // Storage keys
  private readonly AUTH_TOKEN_KEY = 'userId';
  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly USER_DATA_KEY = 'userDetails';

  constructor(private http: HttpClient) { }

  /**
   * Check if user has authentication token
   */
  private hasToken(): boolean {
    return !!localStorage.getItem(this.AUTH_TOKEN_KEY);
  }

  /**
   * Register first step - submit basic info
   * @param userData Basic user registration data
   */
  signUp(userData: SignupRequest): Observable<AuthResponse> {
    this.registrationData = { ...this.registrationData, ...userData };

    return this.http.post<AuthResponse>(`${this.baseUrl}/profile/sign-up`, userData)
      .pipe(catchError(this.handleError));
  }

  signup(userDetails: any): Observable<any> {

    return this.http.post(`${this.baseUrl}/profile-customer/api/v1/dubai/signup`, userDetails);

  }

  /**
   * Request login OTP
   * @param otpRequest OTP request data
   */
  requestLoginOtp(otpRequest: LoginOtpRequest): Observable<AuthResponse> {
    // const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    // const headers = this.getAuthHeaders(accessToken);

    return this.http.post<AuthResponse>(`${this.baseUrl}/profile/sign-in`, otpRequest)
      .pipe(catchError(this.handleError));
  }

  /**
   * Verify OTP with customer API
   * @param data OTP verification data
   */
  verifyOTP(data: OtpVerificationRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/profile-customer/validate-otp`, data)
      .pipe(catchError(this.handleError));
  }

  /**
   * Verify OTP with partner API
   * @param otp OTP code to verify
   */
  verifyOtpForPartner(otp: string): Observable<any> {
    const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const headers = this.getAuthHeaders(accessToken);

    const payload = {
      otp,
      type: "1"  // Static type as required
    };

    return this.http.post(`${this.partnerUrl}/customer/verify-otp`, payload, { headers })
      .pipe(catchError(this.handleError));
  }

  /**
   * Resend OTP to user's mobile
   * @param mobileNumber User's mobile number
   */
  resendOTP(mobileNumber: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/resend-otp`, { mobileNumber })
      .pipe(catchError(this.handleError));
  }

  /**
   * Update user personal details
   * @param userDetails User's personal information
   */
  updateUserDetails(userDetails: UserDetailsRequest): Observable<AuthResponse> {
    this.registrationData = { ...this.registrationData, ...userDetails };

    return this.http.post<AuthResponse>(`${this.baseUrl}/profile/update-details`, userDetails)
      .pipe(catchError(this.handleError));
  }

  /**
   * Update employment details
   * @param employmentDetails User's employment information
   */
  updateEmploymentDetails(employmentDetails: EmploymentDetailsRequest): Observable<AuthResponse> {
    this.registrationData = { ...this.registrationData, ...employmentDetails };

    return this.http.post<AuthResponse>(`${this.baseUrl}/update-employment-details`, employmentDetails)
      .pipe(catchError(this.handleError));
  }

  /**
   * Set password and complete registration
   * @param passwordData User's password information
   */
  setPassword(passwordData: PasswordRequest): Observable<AuthResponse> {
    const finalData = { ...this.registrationData, ...passwordData };

    return this.http.post<AuthResponse>(`${this.baseUrl}/complete-registration`, finalData)
      .pipe(
        tap(response => this.handleAuthResponse(response)),
        catchError(this.handleError)
      );
  }

  /**
   * Login with email and password
   * @param loginData Login credentials
   */
  login(loginData: LoginRequest): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.baseUrl}/login`, loginData)
      .pipe(
        tap(response => this.handleAuthResponse(response)),
        catchError(this.handleError)
      );
  }


  // Add this method to your AuthService class

  /**
   * Check authentication status on app initialization
   */
  checkAuthStatus(): void {
    const token = localStorage.getItem(this.AUTH_TOKEN_KEY);

    if (token) {
      this.isAuthenticatedSubject.next(true);

    } else {
      this.isAuthenticatedSubject.next(false);
    }
  }


  /**
   * Handle successful authentication response
   * @param response Authentication response from server
   */
  private handleAuthResponse(response: AuthResponse): void {
    if (response.success && response.token) {
      localStorage.setItem(this.AUTH_TOKEN_KEY, response.token);
      if (response.userData) {
        localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(response.userData));
      }
      this.isAuthenticatedSubject.next(true);
      // Clear registration data after successful completion
      this.registrationData = {};
    }
  }

  /**
   * Logout the current user
   */

  logout(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.AUTH_TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    this.isAuthenticatedSubject.next(false);

  }

  /**
   * Get current authenticated user data
   */
  getCurrentUser(): any {
    const userData = localStorage.getItem(this.USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Generate partner API access token
   */
  generateAccessToken(): Observable<any> {
    const tokenRequest = {
      grant_type: 'client_credentials',
      client_id: 'finqyqapp',
      client_secret: 'finqyQAPp190523',
      user: '',
      scope: 'userinfo cloud file node',
    };

    return this.http.post(this.authUrl, tokenRequest)
      .pipe(
        tap((response: any) => {
          if (response?.access_token) {
            localStorage.setItem(this.ACCESS_TOKEN_KEY, response.access_token);
          }
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get authentication token
   */
  getToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  /**
   * Get Q Report data
   * @param reportData Report parameters
   */
  getQReport(reportData: any = {}): Observable<any> {
    const accessToken = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    const headers = this.getAuthHeaders(accessToken);

    return this.http.post(`${this.partnerUrl}/lead/q_report`, reportData, { headers })
      .pipe(catchError(this.handleError));
  }

  downloadQReport(reportData: any = {}): Observable<any> {


    return this.http.post(`${this.baseUrl}/report/generate/in`, reportData)
      .pipe(catchError(this.handleError));
  }

  /**
   * Create authentication headers
   * @param token Authentication token
   */
  private getAuthHeaders(token: string | null): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error
   */
  private handleError(error: any): Observable<never> {
    console.error('API error:', error);
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else if (error.status) {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }

    return throwError(() => new Error(errorMessage));
  }

  // Add this method to your AuthService class
  generateQReport(formData: FormData) {
    return this.http.post(`${this.baseUrl}/q-report`, formData);
  }
}